import { useState, useMemo } from 'react';
import { Campaign } from '../config/campaigns';
import { DonationRecord } from '../config/donations';

interface DonationHistoryProps {
    readonly donations: readonly DonationRecord[];
    readonly campaigns: readonly Campaign[];
    readonly walletHex: string;
    readonly onClose: () => void;
}

export function DonationHistory({ donations, campaigns, walletHex, onClose }: DonationHistoryProps) {
    const [activeTab, setActiveTab] = useState<'sent' | 'received'>('sent');

    const sent = useMemo(() => {
        if (!walletHex) return [];
        return donations.filter((d) => d.donorAddress.toLowerCase() === walletHex.toLowerCase());
    }, [donations, walletHex]);

    const received = useMemo(() => {
        if (!walletHex) return [];
        // Find campaigns created by this wallet
        const myCampaignIds = new Set(
            campaigns
                .filter((c) => c.creatorAddress.toLowerCase() === walletHex.toLowerCase())
                .map((c) => c.id.toString()),
        );
        if (myCampaignIds.size === 0) return [];
        // Donations to those campaigns are "received"
        return donations.filter((d) => myCampaignIds.has(d.campaignId.toString()));
    }, [donations, campaigns, walletHex]);

    const items: readonly DonationRecord[] = activeTab === 'sent' ? sent : received;

    const campaignTitleMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const c of campaigns) {
            map.set(c.id.toString(), c.title);
        }
        return map;
    }, [campaigns]);

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal">
                <div className="modal__header">
                    <h2 className="modal__title">Donation History</h2>
                    <button className="modal__close" onClick={onClose} aria-label="Close">
                        x
                    </button>
                </div>

                {!walletHex ? (
                    <div className="donation-history__empty">
                        <p>Connect your wallet to view donation history.</p>
                    </div>
                ) : (
                    <>
                        <div className="donation-history__tabs">
                            <button
                                className={`donation-history__tab${activeTab === 'sent' ? ' donation-history__tab--active' : ''}`}
                                onClick={() => setActiveTab('sent')}
                            >
                                Sent ({sent.length})
                            </button>
                            <button
                                className={`donation-history__tab${activeTab === 'received' ? ' donation-history__tab--active' : ''}`}
                                onClick={() => setActiveTab('received')}
                            >
                                Received ({received.length})
                            </button>
                        </div>

                        {items.length === 0 ? (
                            <div className="donation-history__empty">
                                <p>
                                    {activeTab === 'sent'
                                        ? 'No donations sent yet.'
                                        : 'No donations received yet.'}
                                </p>
                            </div>
                        ) : (
                            <div className="donation-history__list">
                                {items.map((d) => (
                                    <div
                                        key={d.id.toString()}
                                        className="donation-history__item"
                                    >
                                        <div className="donation-history__item-top">
                                            <span className="donation-history__item-title">
                                                {campaignTitleMap.get(d.campaignId.toString()) ?? `Campaign #${d.campaignId.toString()}`}
                                            </span>
                                            <span className="donation-history__item-amount">
                                                {d.amountBTC.toFixed(4)} BTC
                                            </span>
                                        </div>
                                        <div className="donation-history__item-bottom">
                                            {d.txId ? (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    {d.blockHeight === 0n && (
                                                        <span
                                                            style={{
                                                                fontSize: '10px',
                                                                color: 'var(--color-warning, #f5a623)',
                                                                background: 'rgba(245,166,35,0.12)',
                                                                padding: '1px 6px',
                                                                borderRadius: '4px',
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            Pending
                                                        </span>
                                                    )}
                                                    <a
                                                        href={`https://mempool.opnet.org/testnet4/tx/${d.txId}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            fontSize: 'var(--font-size-xs)',
                                                            color: 'var(--text-accent)',
                                                            textDecoration: 'none',
                                                        }}
                                                    >
                                                        TX: {d.txId.slice(0, 12)}...{d.txId.slice(-6)} &#x2197;
                                                    </a>
                                                </span>
                                            ) : (
                                                <span
                                                    style={{
                                                        fontSize: 'var(--font-size-xs)',
                                                        color: 'var(--text-muted)',
                                                    }}
                                                >
                                                    Block #{d.blockHeight.toString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
